import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "store/authStore";
import { useNavigate } from "react-router-dom";
import useModalStore from "store/modalStore";
import { Box, Typography } from "@mui/material";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react";
import Grid from "@mui/material/Grid2";
import CustomButton from "components/Button";
import DuckFootIcon from "assets/icons/DuckFootIcon";

interface IHeroProps {
  blok: { headline: string; image: any; button_name: string; button_link: string; items: any };
}

const Hero = ({ blok }: IHeroProps) => {
  const { getUserAuthStatus } = useAuthStore();
  const { openModal } = useModalStore();
  const navigate = useNavigate();
  const heroSectionRef = useRef(null);
  const [showIcon1, setShowIcon1] = useState(false);
  const [showIcon2, setShowIcon2] = useState(false);
  const [showIcon3, setShowIcon3] = useState(false);
  const [showIcon4, setShowIcon4] = useState(false);
  const heroImagePath = blok?.image?.filename || "";
  const isUserAuthenticated = getUserAuthStatus();

  const handleHeroButtonClick = () => {
    if (!isUserAuthenticated) {
      openModal("signup");
      return;
    }

    navigate("/pwa/create");
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => setShowIcon1(true), 0);
            setTimeout(() => setShowIcon2(true), 500);
            setTimeout(() => setShowIcon3(true), 1000);
            setTimeout(() => setShowIcon4(true), 1500);

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.66 }
    );

    if (heroSectionRef.current) {
      observer.observe(heroSectionRef.current);
    }

    return () => {
      if (heroSectionRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(heroSectionRef.current);
      }
    };
  }, []);

  return (
    <Grid
      container
      component="section"
      spacing={4}
      ref={heroSectionRef}
      {...storyblokEditable(blok)}
      sx={{ position: "relative", marginBottom: { xs: "100px", md: "40px" } }}
    >
      <Grid
        size={{ xs: 12, md: 6, lg: 7 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "374px",
          borderRadius: "16px",
          padding: { xs: "36px", md: "48px" },
          backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)),url(${heroImagePath})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          component="h1"
          sx={{
            textAlign: "left",
            color: "#FFFFFF",
            fontWeight: 500,
            fontSize: { xs: "36px", md: "40px" },
            lineHeight: { xs: "45px", md: "44px" },
          }}
        >
          {blok.headline}
        </Typography>
        <CustomButton
          width={"max-content"}
          height={50}
          textButton={blok.button_name}
          customBgColor="#F4701B"
          customHoverColor="#f0670b"
          customBorderRadius="8px"
          customFontSize="18px"
          customPadding="16px 24px"
          customTextTransform="none"
          onClick={() => handleHeroButtonClick()}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 5 }}>
        {blok?.items?.map((blok: any) => <StoryblokComponent blok={blok} key={blok._uid} />)}
      </Grid>
      {showIcon1 && (
        <Box
          className="icon-animation"
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            transform: {
              xs: "rotate(150deg) translate(38px, -34px)",
              md: isUserAuthenticated
                ? "rotate(160deg) translate(10px, -46px)"
                : "rotate(49deg) translate(50px, -55px)",
            },
            opacity: showIcon1 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          <DuckFootIcon />
        </Box>
      )}
      {showIcon2 && (
        <Box
          className="icon-animation"
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            transform: {
              xs: "rotate(130deg) translate(110px, 20px)",
              md: isUserAuthenticated ? "rotate(125deg) translate(96px, 0px)" : "rotate(92deg) translate(45px, -25px)",
            },
            opacity: showIcon2 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          <DuckFootIcon />
        </Box>
      )}
      {showIcon3 && (
        <Box
          className="icon-animation"
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            transform: {
              xs: "rotate(150deg) translate(160px, 38px)",
              md: isUserAuthenticated
                ? "rotate(151deg) translate(129px, 17px)"
                : "rotate(150deg) translate(80px, -4px)",
            },
            opacity: showIcon3 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          <DuckFootIcon />
        </Box>
      )}
      {showIcon4 && (
        <Box
          className="icon-animation"
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            transform: {
              xs: "rotate(130deg) translate(200px, 120px)",
              md: isUserAuthenticated
                ? "rotate(129deg) translate(176px, 90px)"
                : "rotate(130deg) translate(138px, 56px)",
            },
            opacity: showIcon4 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          <DuckFootIcon />
        </Box>
      )}
    </Grid>
  );
};

export default Hero;
